// Association portal: which of the administered associations the portal is
// showing. The choice is remembered per user for the browser session only
// (sessionStorage), so a reload keeps it and a new tab starts clean.

const storageKey = (userId: number) => `associationId:${userId}`;

export const readRememberedAssociationId = (userId: number): number | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (raw == null) {
      return null;
    }
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

export const rememberAssociationId = (
  userId: number,
  id: number | null,
): void => {
  try {
    if (id == null) {
      sessionStorage.removeItem(storageKey(userId));
    } else {
      sessionStorage.setItem(storageKey(userId), String(id));
    }
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the
    // selection then simply lives in Redux for this page load.
  }
};

/**
 * Selection rule after every load: keep the current id if it is still in the
 * list, else the remembered id if it is, else the first row; nothing when the
 * list is empty.
 */
export const resolveSelectedAssociationId = (
  associations: ReadonlyArray<{ id: number }>,
  currentId: number | null,
  rememberedId: number | null,
): number | null => {
  if (associations.length === 0) {
    return null;
  }
  const has = (id: number | null) =>
    id != null && associations.some((association) => association.id === id);
  if (has(currentId)) {
    return currentId;
  }
  if (has(rememberedId)) {
    return rememberedId;
  }
  return associations[0].id;
};
